import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { resolvedFilingThreadSchema } from "../accounts-receivable/collection-evidence/resolve-collection-filing-target.step";

/**
 * Detect Conversation Reply Step
 *
 * Deterministically matches an inbound email to an agent-managed email
 * conversation (comm_agent_email_conversations) — "is this a reply to an
 * email the agent is tracking?" — using the Graph identifiers resolved at
 * ingest (linked outbound email / conversation id). No LLM, no content
 * guessing; unrelated email simply doesn't match and every downstream step
 * no-ops (empty filing list, suggest step skips).
 *
 * On a match the conversation flips to reply_received and the step emits the
 * topic's filing targets, shaped to feed straight into
 * file_collection_evidence.
 */
export const detectConversationReplyStep = defineStepConfig({
  type: "detect_conversation_reply",
  name: "Detect Conversation Reply",
  description:
    "Matches an inbound email to an agent-managed email conversation awaiting a reply; emits the topic's filing targets",
  category: "Data Access",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to match against tracked conversations",
    ),
  }),

  outputSchema: z.object({
    matched: z
      .boolean()
      .describe("True when the email replies to a tracked conversation"),
    agentConversationId: z
      .uuid()
      .nullable()
      .describe("Matched conversation id; null when unmatched"),
    topic: z
      .string()
      .nullable()
      .describe(
        "Conversation topic (e.g. collection_thread); null when unmatched",
      ),
    topicEntityId: z
      .uuid()
      .nullable()
      .describe("Id of the topic's domain entity; null when unmatched"),
    threads: z
      .array(resolvedFilingThreadSchema)
      .describe(
        "Filing targets for the reply, ready to feed file_collection_evidence; empty when unmatched",
      ),
    filedBy: z
      .enum(["email", "auto_match"])
      .describe(
        "Always 'auto_match' — wired into file_collection_evidence so the system filing is tagged correctly",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,
});
